/**
 * PATH: packages/ui/src/mobile/mobileDockSignals.ts
 * FILE_ID: UI-MOBILE-DOCK-SIGNALS-001
 * PURPOSE: One page <-> shell channel for the mobile dock: a page asks for the
 *      floor (auto-collapse) or hosts the restore control in its own chrome.
 * LANDMINE: the shell never sniffs page URLs. Claims are reference-counted
 *      attributes on <html>, so two mounts cannot clear each other's claim.
 *      Each 0->1 claim writes a fresh generation token, so the shell can tell a
 *      remount (QA job A -> job B in one batch) from a claim that never ended.
 */
import { useCallback, useEffect, useLayoutEffect, useSyncExternalStore } from 'react'

/** Claims land before first paint so the dock never paints expanded, then slides. */
const useIsoLayoutEffect = typeof window !== 'undefined' ? useLayoutEffect : useEffect

export const MOBILE_DOCK_AUTO_COLLAPSE_ATTR = 'data-mobile-dock-auto-collapse'
export const MOBILE_DOCK_RESTORE_HOST_ATTR = 'data-mobile-dock-restore-host'
/** Published by the dock itself while it is collapsed on a narrow screen. */
export const MOBILE_DOCK_COLLAPSED_ATTR = 'data-mobile-dock-collapsed'
export const MOBILE_DOCK_RESTORE_EVENT = 'quotech:mobile-dock-restore'

const claims = new Map<string, number>()
let generation = 0

function claimRootAttr(attr: string): () => void {
  const root = document.documentElement
  const count = (claims.get(attr) ?? 0) + 1
  claims.set(attr, count)
  if (count === 1) root.setAttribute(attr, String(++generation))
  return () => {
    const left = (claims.get(attr) ?? 1) - 1
    if (left > 0) {
      claims.set(attr, left)
      return
    }
    claims.delete(attr)
    root.removeAttribute(attr)
  }
}

/** Live value of an attribute on <html>; null when absent or during SSR. */
export function useRootAttr(attr: string): string | null {
  const subscribe = useCallback((onChange: () => void) => {
    if (typeof MutationObserver === 'undefined') return () => {}
    const observer = new MutationObserver(onChange)
    observer.observe(document.documentElement, { attributes: true, attributeFilter: [attr] })
    return () => observer.disconnect()
  }, [attr])
  return useSyncExternalStore(
    subscribe,
    () => document.documentElement.getAttribute(attr),
    () => null,
  )
}

/** A page that needs the whole floor asks the shell to collapse its dock while mounted. */
export function useRequestMobileDockCollapse(active = true): void {
  useIsoLayoutEffect(() => {
    if (!active || typeof document === 'undefined') return undefined
    return claimRootAttr(MOBILE_DOCK_AUTO_COLLAPSE_ATTR)
  }, [active])
}

/** A page that shows the dock's restore control in its own chrome. `visible`
 *  is true only while a shell dock is actually collapsed. */
export function useMobileDockRestoreHost(active = true): { visible: boolean; restore: () => void } {
  useIsoLayoutEffect(() => {
    if (!active || typeof document === 'undefined') return undefined
    return claimRootAttr(MOBILE_DOCK_RESTORE_HOST_ATTR)
  }, [active])
  const collapsed = useRootAttr(MOBILE_DOCK_COLLAPSED_ATTR) === 'true'
  const restore = useCallback(() => {
    window.dispatchEvent(new Event(MOBILE_DOCK_RESTORE_EVENT))
  }, [])
  return { visible: active && collapsed, restore }
}
